import type { EmailCadence } from '@developerz/domain';
import { useQueryClient } from '@tanstack/solid-query';
import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { canManageTeamEmail } from '../../lib/emailRoutes';
import { createMutation } from '../../lib/mutation';
import {
  presetEstimates,
  refreshNotificationEstimate,
  useNotificationEstimate,
} from '../../lib/notification-estimate';
import type { ShownTier } from '../../lib/notification-labels';
import {
  type NotificationCellWrite,
  type NotificationSettingsRow,
  type NotificationSettingsWrite,
  replaceNotificationSettings,
  setCadence,
  useNotificationSettings,
} from '../../lib/notification-settings';
import { groupByTier } from '../../lib/notification-tiers';
import { roleSettled } from '../../lib/roles';
import { useMe } from '../../lib/session';
import { QueryBoundary, type QueryLike } from '../QueryBoundary';
import { Skeleton } from '../Skeleton';
import { NotificationAdvanced } from './NotificationAdvanced';
import { NotificationCadence } from './NotificationCadence';
import { NotificationPresets } from './NotificationPresets';
import { NotificationSendTo } from './NotificationSendTo';
import { NotificationTestEmail } from './NotificationTestEmail';
import { NotificationTierCard } from './NotificationTierCard';

// The Notifications page's container: the ONE settings read, the ONE
// preset/cell write through a `createMutation` whose `onSettled` re-reads, and
// a SEPARATE cadence-only save mutation. The cadence section is purely
// presentational: it receives the cadence, the busy flag and the error
// straight from this container, so mounting the page does not need a second
// settings observer off the child. Presets, a card per tier, delivery timing,
// Send to with the test email after it, the browser slot, Advanced.
//
// EVERY WRITE IS FOLLOWED BY A RE-READ and nothing is painted from a click: a
// preset, a tier flip and a single switch all go through one mutation whose
// `onSettled` re-reads; the cadence PUT goes through ITS OWN mutation for
// the same reason and to keep cadence writes off the preset/cell FIFO queue
// — radios only fire on `change`, so a true double click is impossible and a
// second-click dedupe is not needed. A refusal shows the server's own
// `detail`.
//
// THE SECTIONS REPAINT IN PLACE, and that is why `boundary` below exists.
// `<QueryBoundary/>` calls its children with `query.data`, so handing it the
// real data would rebuild every section on each re-read and throw the keyboard
// focus off the switch that was just flipped. It is handed a query whose `data`
// only says THAT the settings loaded (a memo that never changes again); the
// sections read `query.data` through accessors and iterate stable ids.
//
// NO CONTROL IS DISABLED DURING A WRITE, for the same reason: disabling a
// focused control drops focus. A second click whose body is equal to the one
// submitted just before it (the other half of a double click) is dropped; a
// DIFFERENT one is queued and sent in order (below), and `aria-busy` says that
// a save is on its way.
//
// `browser` is the browser section, composed by the route. It sits between
// Send to and Advanced and outside the boundary: it does not depend on this
// read, so a failed read must not take it off the screen.

export function NotificationTiers(props: {
  token: string | null;
  onNotice?: (msg: string) => void;
  browser?: JSX.Element;
}): JSX.Element {
  const query = useNotificationSettings(props.token);
  const me = useMe(props.token);
  const queryClient = useQueryClient();
  // The same estimate the header shows (`NotificationEstimate`): same query
  // key, so this is a CACHE read, not a second request. It feeds the preset
  // buttons their per-preset numbers ("about 2 a week").
  const estimate = useNotificationEstimate(props.token);

  const write = createMutation({
    call: (body: NotificationSettingsWrite) => replaceNotificationSettings(body, props.token),
    fallback: t('notifications_settings.save_error'),
    onSuccess: () => props.onNotice?.(t('notifications_settings.saved_notice')),
    onError: (message) => props.onNotice?.(message),
    // The settings re-read is AWAITED and RETURNED so `createMutation` holds
    // `pending` (and the queue below) until it lands: the next write is
    // computed from fresh settings. The estimate refresh
    // (`refreshNotificationEstimate`, wired into every settings write) starts
    // beside it and is NOT awaited — a read carries no timeout (`lib/api.ts`),
    // so a hung estimate GET must not wedge writes, and its rejection is
    // caught so it cannot surface as unhandled either.
    onSettled: () => {
      void refreshNotificationEstimate(queryClient).catch(() => {});
      return query.refetch();
    },
  });

  // A cadence write is its own `createMutation`, deliberately SEPARATE from
  // the preset/cell mutation above: a cadence click must not wait on a queued
  // preset/cell write, a refused cadence must not surface as a generic notice,
  // and radios only fire on `change` so a second click of a double click never
  // arrives — `createMutation`'s own `inFlight` guard is enough. Its
  // `onSettled` does the same re-read-and-refresh as the preset/cell write.
  const cadenceSave = createMutation({
    call: (next: EmailCadence) => setCadence(next, props.token),
    fallback: t('notifications_settings.timing.save_error'),
    onSettled: () => {
      void refreshNotificationEstimate(queryClient).catch(() => {});
      return query.refetch();
    },
  });

  // The write queue. `createMutation` drops a call made while one is in flight
  // — right for the second click of a double click, which computes the same
  // body — but this page offers many controls at once, and a DIFFERENT flip
  // made inside the PUT-plus-re-read window used to be dropped with no notice
  // and no visible change. Distinct bodies therefore queue FIFO: a preset's
  // `replaceAll` lands before a later cell, and each write keeps its own save
  // notice and its own re-read through the mutation's own handlers, because
  // `drain` only starts the next body once the previous `run` (whose `finally`
  // awaits the re-read) has settled.
  let inFlight: NotificationSettingsWrite | null = null;
  const queued: NotificationSettingsWrite[] = [];

  function drain(): void {
    inFlight = null;
    const next = queued.shift();
    if (next !== undefined) start(next);
  }

  function start(body: NotificationSettingsWrite): void {
    inFlight = body;
    // `run` does not reject today, but `drain` on the rejection path too, so a
    // change there can never strand the queue.
    void write.run(body).then(drain, drain);
  }

  /** One write, now or in order: a body equal to the one submitted just before it is dropped. */
  function submit(body: NotificationSettingsWrite): void {
    if (inFlight === null) {
      start(body);
      return;
    }
    const last = queued.length > 0 ? queued[queued.length - 1] : inFlight;
    if (JSON.stringify(last) === JSON.stringify(body)) {
      return;
    }
    queued.push(body);
  }

  const loaded = createMemo(() => query.data !== undefined);
  const boundary: QueryLike<true> = {
    get isLoading() {
      return query.isLoading;
    },
    get isError() {
      return query.isError;
    },
    get error() {
      return query.error;
    },
    get data() {
      return loaded() ? (true as const) : undefined;
    },
    refetch: () => query.refetch(),
  };

  // The cadence the cadence section is told about. Wrapped in a memo so
  // Cadence's bindings share a single tracked source — the memo's default
  // equals propagates a cadence string flip as a real notification, the
  // way `query.data?.cadence` does for `<NotificationPresets>`'s preset
  // button below.
  const cadence = createMemo(() => query.data?.cadence);

  const rows = (): readonly NotificationSettingsRow[] => query.data?.matrix ?? [];
  const groups = createMemo(() => groupByTier(rows()));
  const tiers = createMemo(() => groups().map((group) => group.tier));
  const rowsOf = (tier: ShownTier): readonly NotificationSettingsRow[] =>
    groups().find((group) => group.tier === tier)?.rows ?? [];
  const send = (cells: readonly NotificationCellWrite[]): void => submit({ cells });
  const showTeam = (): boolean => roleSettled(me) && canManageTeamEmail(me.data?.role);

  return (
    <div class="notification-settings" aria-busy={write.pending()}>
      <QueryBoundary query={boundary} loading={<Skeleton height="24rem" />}>
        {() => (
          <>
            <NotificationPresets
              preset={query.data?.preset ?? null}
              onPick={(preset) => submit({ preset })}
              estimates={presetEstimates(estimate.data)}
              floor={estimate.data?.truncated ?? false}
            />
            <For each={tiers()}>
              {(tier) => <NotificationTierCard tier={tier} rows={rowsOf(tier)} onWrite={send} />}
            </For>
            {/* Delivery timing (slice 20, #5705) before Send to, and the test
                email right after it — the spec's page order, which the browser
                slot below would otherwise break: mounted after this component,
                both would land BELOW it. Cadence is a pure presenter: the
                cadence, the busy flag and the error are read here, on this
                page's only settings observer — Cadence itself instantiates
                none. */}
            <NotificationCadence
              cadence={cadence}
              pending={cadenceSave.pending}
              error={cadenceSave.error}
              onChange={(next) => cadenceSave.run(next)}
            />
            <NotificationSendTo
              address={query.data?.delivery_address ?? null}
              showTeam={showTeam()}
              token={props.token}
            />
            <NotificationTestEmail token={props.token} />
          </>
        )}
      </QueryBoundary>
      {props.browser}
      <Show when={loaded() && !query.isError}>
        <NotificationAdvanced rows={rows()} onWrite={send} />
      </Show>
      {/* Slice 21 (#5706) fills two slots on the page, and this is the second:
          the recent-deliveries list, after Advanced (the first is the weekly
          estimate under the header). */}
    </div>
  );
}
